import { Browsers } from 'shared/enums/Browsers';
import { BrowserSupportFeatures } from 'shared/enums/BrowserSupportFeatures';
import { CallTypes } from 'shared/enums/CallTypes';
import { OS } from 'shared/enums/OperatingSystems';
import { toSentenceList } from 'shared/utils';
import UAParser from 'ua-parser-js';

interface IBrowserSupported {
  OS: string;
  browser: string;
  isBrowserSupported: boolean;
  supportedBrowsers: string[];
}

// There's a difference between the supported browsers with the Twilio voice SDK and video JS, so we need to check both.
// Voice: https://www.twilio.com/docs/voice/sdks/javascript#supported-browsers
// Video: https://www.twilio.com/docs/video/javascript#supported-browsers
const supportedBrowserMapVoice: Record<string, Record<string, boolean>> = {
  [OS.Android]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true },
  [OS.iOS]: { [Browsers.MobileSafari]: true },
  [OS.Linux]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true },
  [OS.MacOS]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true, [Browsers.Safari]: true, [Browsers.Edge]: true },
  [OS.Windows]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true, [Browsers.Edge]: true },
  [OS.ChromiumOS]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true, [Browsers.Edge]: true },
};

export const supportedBrowserMapVideo: Record<string, Record<string, boolean>> = {
  [OS.Android]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true },
  [OS.iOS]: { [Browsers.MobileSafari]: true, [Browsers.Chrome]: true },
  [OS.Linux]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true },
  [OS.MacOS]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true, [Browsers.Safari]: true, [Browsers.Edge]: true },
  [OS.Windows]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true, [Browsers.Edge]: true },
  [OS.ChromiumOS]: { [Browsers.Chrome]: true, [Browsers.Firefox]: true, [Browsers.Edge]: true },
};

const supportedBrowserMapScreenshot: Record<string, Record<string, boolean>> = {
  [OS.MacOS]: { [Browsers.Chrome]: true, [Browsers.Edge]: true },
  [OS.Windows]: { [Browsers.Chrome]: true, [Browsers.Edge]: true },
};

const getSupportedBrowserMap = (feature: BrowserSupportFeatures): Record<string, Record<string, boolean>> => {
  switch (feature) {
    case BrowserSupportFeatures.Video:
      return supportedBrowserMapVideo;
    case BrowserSupportFeatures.Screenshot:
      return supportedBrowserMapScreenshot;
    case BrowserSupportFeatures.Voice:
    default:
      return supportedBrowserMapVoice;
  }
};

export const getTooltipMessage = (callType: CallTypes, userOS: string, micDisabled?: boolean): string => {
  if (micDisabled)
    return 'Please give your browser permission to access your microphone and refresh the page to make phone calls.';

  const supportedBrowsersList = toSentenceList(getSupportedBrowsers(), false);
  return `${userOS} devices do not currently support ${callType} calls in this browser. Please open Flow in your ${supportedBrowsersList} web browser to make a call.`;
};

export const getSupportedBrowsers = (feature = BrowserSupportFeatures.Voice): string[] => {
  const { getOS } = new UAParser();
  const OS: string = getOS().name || '';
  const map = getSupportedBrowserMap(feature);
  return Object.keys(map[OS]).filter((browser: string) => !!map[OS][browser]);
};

const checkBrowserSupport = (feature = BrowserSupportFeatures.Voice): IBrowserSupported => {
  const { getBrowser, getOS } = new UAParser();
  const OS: string = getOS().name || '';
  const browser: string = getBrowser().name || '';
  let isBrowserSupported: boolean;
  let supportedBrowsers: string[] = [];

  const map = getSupportedBrowserMap(feature);

  if (!OS || !browser || !map[OS]) {
    isBrowserSupported = false;
  } else {
    isBrowserSupported = map[OS][browser];
    supportedBrowsers = getSupportedBrowsers(feature);
  }

  return { OS, browser, isBrowserSupported, supportedBrowsers };
};

export default checkBrowserSupport;
